import React, { useState, useEffect, useMemo } from 'react';
// @ts-expect-error react-rnd 7.3 ships no types
import Rnd from 'react-rnd';
import { useThemePreference, ThemePreference } from 'services/ThemePreference';
import LocalStorage, { makeKey } from '../../services/LocalStorage';

const STORAGE_KEY = makeKey<SavedState>('colorTokenInspector:v1');
const DRAG_HANDLE_CLASS = 'color-token-inspector-drag-handle';

type TokenGroup = {
  name: string;
  isInput?: boolean;
  tokens: string[];
};

const TOKEN_GROUPS: TokenGroup[] = [
  {
    name: 'Brand inputs (numbers)',
    isInput: true,
    tokens: [
      '--brand-primary-h',
      '--brand-primary-c',
      '--brand-secondary-h',
      '--brand-secondary-c',
      '--brand-tertiary-h',
      '--brand-tertiary-c',
    ],
  },
  {
    name: 'Primary scale',
    tokens: [
      '--primary-50',
      '--primary-100',
      '--primary-200',
      '--primary-300',
      '--primary-400',
      '--primary-500',
      '--primary-600',
      '--primary-700',
      '--primary-800',
      '--primary-900',
    ],
  },
  {
    name: 'Secondary scale',
    tokens: [
      '--secondary-50',
      '--secondary-100',
      '--secondary-200',
      '--secondary-300',
      '--secondary-400',
      '--secondary-500',
      '--secondary-600',
      '--secondary-700',
      '--secondary-800',
      '--secondary-900',
    ],
  },
  {
    name: 'Tertiary scale',
    tokens: [
      '--tertiary-50',
      '--tertiary-100',
      '--tertiary-200',
      '--tertiary-300',
      '--tertiary-400',
      '--tertiary-500',
      '--tertiary-600',
      '--tertiary-700',
      '--tertiary-800',
      '--tertiary-900',
    ],
  },
  {
    name: 'Neutrals',
    tokens: [
      '--neutral-0',
      '--neutral-25',
      '--neutral-50',
      '--neutral-75',
      '--neutral-100',
      '--neutral-200',
      '--neutral-300',
      '--neutral-400',
      '--neutral-500',
      '--neutral-600',
      '--neutral-700',
      '--neutral-800',
      '--neutral-900',
      '--neutral-1000',
    ],
  },
  {
    name: 'Status primitives',
    tokens: [
      '--success-100',
      '--success-400',
      '--success-500',
      '--success-600',
      '--success-700',
      '--danger-100',
      '--danger-500',
      '--danger-600',
      '--danger-700',
      '--warning-100',
      '--warning-500',
      '--warning-700',
      '--info-100',
      '--info-500',
      '--color-accent-orange',
    ],
  },
  {
    name: 'Semantic — Surfaces',
    tokens: [
      '--color-bg-page',
      '--color-bg-surface',
      '--color-bg-elevated',
      '--color-bg-muted',
      '--color-bg-subtle',
    ],
  },
  {
    name: 'Semantic — Text',
    tokens: [
      '--color-text-primary',
      '--color-text-secondary',
      '--color-text-muted',
      '--color-text-disabled',
      '--color-text-on-action',
      '--color-text-heading',
    ],
  },
  {
    name: 'Semantic — Borders',
    tokens: ['--color-border-default', '--color-border-strong', '--color-border-focus'],
  },
  {
    name: 'Semantic — Action',
    tokens: [
      '--color-action-primary',
      '--color-action-primary-hover',
      '--color-action-primary-active',
      '--color-action-primary-bg',
      '--color-action-secondary',
      '--color-action-secondary-hover',
      '--color-action-secondary-active',
      '--color-action-secondary-bg',
      '--color-action-tertiary',
      '--color-action-tertiary-hover',
      '--color-action-tertiary-active',
      '--color-action-tertiary-bg',
    ],
  },
  {
    name: 'Semantic — Status',
    tokens: [
      '--color-status-success',
      '--color-status-success-bg',
      '--color-status-danger',
      '--color-status-danger-bg',
      '--color-status-warning',
      '--color-status-warning-bg',
      '--color-status-info',
      '--color-status-info-bg',
      '--color-status-neutral',
      '--color-status-neutral-bg',
    ],
  },
];

type SavedState = {
  x: number;
  y: number;
  width: number;
  height: number;
  minimized: boolean;
};

function getDefaultState(): SavedState {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
  return {
    x: Math.max(20, w - 360),
    y: 80,
    width: 340,
    height: 560,
    minimized: false,
  };
}

function loadState(): SavedState {
  try {
    const state = LocalStorage.get(STORAGE_KEY);
    if (!state) return getDefaultState();
    return { ...getDefaultState(), ...state };
  } catch {
    return getDefaultState();
  }
}

function saveState(state: SavedState): void {
  try {
    LocalStorage.set(STORAGE_KEY, state);
  } catch {
    // ignore
  }
}

const ColorTokenInspector: React.FC = () => {
  const [state, setState] = useState<SavedState>(loadState);
  const [search, setSearch] = useState('');
  const [theme, setTheme] = useThemePreference();
  const [copied, setCopied] = useState<string | null>(null);

  const updateState = (patch: Partial<SavedState>): void => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveState(next);
      return next;
    });
  };

  const copyVar = async (token: string): Promise<void> => {
    const text = `var(${token})`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
    setCopied(token);
    setTimeout(() => setCopied((c) => (c === token ? null : c)), 1200);
  };

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return TOKEN_GROUPS;
    const needle = search.toLowerCase();
    return TOKEN_GROUPS.map((g) => ({
      ...g,
      tokens: g.tokens.filter((t) => t.toLowerCase().includes(needle)),
    })).filter((g) => g.tokens.length > 0);
  }, [search]);

  if (state.minimized) {
    return (
      <div onClick={() => updateState({ minimized: false })} style={pillStyle}>
        🎨 Color tokens
      </div>
    );
  }

  return (
    <Rnd
      position={{ x: state.x, y: state.y }}
      size={{ width: state.width, height: state.height }}
      onDragStop={(_e: unknown, d: { x: number; y: number }) => updateState({ x: d.x, y: d.y })}
      onResizeStop={(
        _e: unknown,
        _dir: unknown,
        ref: HTMLElement,
        _delta: unknown,
        position: { x: number; y: number },
      ) =>
        updateState({
          width: ref.offsetWidth,
          height: ref.offsetHeight,
          x: position.x,
          y: position.y,
        })
      }
      minWidth={260}
      minHeight={220}
      bounds="window"
      dragHandleClassName={DRAG_HANDLE_CLASS}
      style={{ zIndex: 10001 }}
    >
      <div style={containerStyle}>
        <div className={DRAG_HANDLE_CLASS} style={headerStyle}>
          <span style={{ flex: 1, fontWeight: 600 }}>🎨 Color tokens</span>
          <div style={segmentedStyle} role="group" aria-label="Theme preference">
            {(['light', 'system', 'dark'] as ThemePreference[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setTheme(mode)}
                style={{
                  ...segmentBtnStyle,
                  ...(theme === mode ? segmentBtnActiveStyle : null),
                }}
                title={`Theme: ${mode}`}
              >
                {mode === 'light' ? '☀' : mode === 'dark' ? '🌙' : '🖥'}
              </button>
            ))}
          </div>
          <button
            onClick={() => updateState({ minimized: true })}
            style={iconBtnStyle}
            title="Minimize"
          >
            —
          </button>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter tokens…"
          style={searchStyle}
        />
        <div style={listStyle}>
          {filteredGroups.map((group) => (
            <div key={group.name} style={{ marginBottom: 4 }}>
              <div style={groupHeaderStyle}>{group.name}</div>
              {group.tokens.map((token) => (
                <TokenRow
                  key={token}
                  token={token}
                  isInput={group.isInput}
                  copied={copied === token}
                  onClick={() => copyVar(token)}
                />
              ))}
            </div>
          ))}
          {filteredGroups.length === 0 && (
            <div style={emptyStyle}>No tokens match &ldquo;{search}&rdquo;</div>
          )}
        </div>
      </div>
    </Rnd>
  );
};

type TokenRowProps = {
  token: string;
  isInput?: boolean;
  copied: boolean;
  onClick: () => void;
};

const TokenRow: React.FC<TokenRowProps> = ({ token, isInput, copied, onClick }) => {
  const [resolvedValue, setResolvedValue] = useState('');
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const read = (): void => {
      const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
      setResolvedValue(v);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'style', 'class'],
    });
    return () => observer.disconnect();
  }, [token]);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...rowStyle, background: hovered ? '#2a2c33' : 'transparent' }}
      title={`Click to copy var(${token})`}
    >
      {isInput ? (
        <div style={{ ...swatchStyle, ...numSwatchStyle }}>num</div>
      ) : (
        <div style={{ ...swatchStyle, background: `var(${token})` }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={tokenNameStyle}>{token}</div>
        <div style={tokenValueStyle}>{resolvedValue || '(unresolved)'}</div>
      </div>
      {copied && <span style={copiedBadgeStyle}>copied</span>}
    </div>
  );
};

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: '#1d1f24',
  color: '#fff',
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  overflow: 'hidden',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '10px 12px',
  background: '#13141a',
  borderBottom: '1px solid var(--color-border-default)',
  cursor: 'move',
  userSelect: 'none',
  fontSize: 13,
};

const iconBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#fff',
  cursor: 'pointer',
  padding: '4px 8px',
  fontSize: 14,
  marginLeft: 4,
  borderRadius: 4,
};

const segmentedStyle: React.CSSProperties = {
  display: 'inline-flex',
  background: '#0f1014',
  border: '1px solid var(--color-border-default)',
  borderRadius: 6,
  marginLeft: 4,
  overflow: 'hidden',
};

const segmentBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#9aa0a6',
  cursor: 'pointer',
  padding: '3px 8px',
  fontSize: 13,
  lineHeight: 1,
};

const segmentBtnActiveStyle: React.CSSProperties = {
  background: '#2a2c33',
  color: '#fff',
};

const searchStyle: React.CSSProperties = {
  margin: '8px 12px',
  padding: '6px 10px',
  background: '#0f1014',
  border: '1px solid var(--color-border-default)',
  borderRadius: 4,
  color: '#fff',
  fontSize: 12,
  outline: 'none',
};

const listStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '4px 8px 12px',
};

const groupHeaderStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#9aa0a6',
  margin: '8px 0 4px',
  paddingTop: 6,
  borderTop: '1px solid var(--color-border-default)',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '5px 6px',
  borderRadius: 4,
  cursor: 'pointer',
};

const swatchStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 4,
  flexShrink: 0,
  border: '1px solid var(--color-border-default)',
  backgroundImage:
    'linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%), linear-gradient(45deg, #444 25%, transparent 25%, transparent 75%, #444 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 4px 4px',
};

const numSwatchStyle: React.CSSProperties = {
  background: '#2a2c33',
  backgroundImage: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  color: '#aaa',
};

const tokenNameStyle: React.CSSProperties = {
  fontSize: 11,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const tokenValueStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#9aa0a6',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const copiedBadgeStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#5bc236',
  fontWeight: 600,
};

const emptyStyle: React.CSSProperties = {
  color: '#888',
  padding: '20px 8px',
  textAlign: 'center',
  fontSize: 12,
};

const pillStyle: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 16,
  zIndex: 10001,
  background: '#1d1f24',
  color: '#fff',
  padding: '8px 14px',
  borderRadius: 999,
  boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
  fontSize: 12,
  fontFamily: 'system-ui, sans-serif',
  cursor: 'pointer',
  userSelect: 'none',
};

export default ColorTokenInspector;
